import { useSelector } from '@xstate/react'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import useQueryParams from 'hooks/useQueryParams'
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type PropsWithChildren } from 'react'
import { useIntl } from 'react-intl'
import { showSnackbar } from 'LuxKit/Snackbar/AppSnackbar'
import { getCartMachineActor, MultiCartContext } from 'machines/cart/MultiCartMachineProvider'
import { successIcon } from 'machines/cart/snackbarUtils'
import { prepareItem } from 'lib/checkout/userCart'
import type { Wedding } from 'weddings/api/weddings'
import useWedding from 'weddings/hooks/useWedding'
import * as sessionStorage from 'lib/storage/isomorphicSessionStorage'
import {
  getWeddingGiftRegistryCartPath,
  MULTI_CART_ID_QUERY_PARAM,
  WEDDING_GIFT_REGISTRY_CART_ID_QUERY_PARAM,
  WEDDING_GIFT_REGISTRY_CART_PATH_REGEXP,
  WEDDING_GIFT_REGISTRY_SESSION_KEY,
  WEDDING_GIFT_REGISTRY_SLUG_QUERY_PARAM,
  WEDDING_SETUP_EDIT_PATH_REGEXP,
} from 'weddings/constants/giftRegistry'
import { isWeddingGiftRegistryItemSupported } from 'weddings/utils/weddingGiftCart'
import { selectEffectiveVisibleCart } from 'machines/cart/selectors'
import ModalContext from 'contexts/ModalContext'
import WeddingGiftRegistryWelcomeModal, { type WeddingGiftRegistryWelcomeModalResult } from 'weddings/components/WeddingGiftRegistryWelcomeModal'
import { replaceWithRegion } from 'actions/NavigationActions'

interface WeddingGiftRegistrySession {
  slug: string
  giftRegistryCartId?: string
}

interface AddItemsOptions {
  skipNotification?: boolean
  onAdd?: (items: Array<App.Checkout.AnyItem>) => void
}

interface WeddingGiftRegistryContextValue {
  isActive: boolean
  isLoading: boolean
  wedding?: Wedding
  giftRegistryCartId?: string
  addItems: (items: Array<App.Checkout.AnyItem>, options?: AddItemsOptions) => boolean
  clearContext: () => void
  addToWeddingGiftRegistryCartCTALabel: string
  addToWeddingGiftRegistryCartCTALabelShort: string
}

const defaultContextValue: WeddingGiftRegistryContextValue = {
  isActive: false,
  isLoading: false,
  wedding: undefined,
  giftRegistryCartId: undefined,
  addItems: () => false,
  clearContext: () => {},
  addToWeddingGiftRegistryCartCTALabel: '',
  addToWeddingGiftRegistryCartCTALabelShort: '',
}

const WeddingGiftRegistryContext = createContext<WeddingGiftRegistryContextValue>(defaultContextValue)

interface Props extends PropsWithChildren {
  value?: WeddingGiftRegistryContextValue
}

function WeddingGiftRegistryProvider({ children, value }: Props) {
  const intl = useIntl()
  const multiCartActorRef = MultiCartContext.useActorRef()

  const queryParams = useQueryParams()
  const querySlug = useMemo(() => queryParams.get(WEDDING_GIFT_REGISTRY_SLUG_QUERY_PARAM) ?? undefined, [queryParams])
  const queryGiftRegistryCartId = useMemo(() => queryParams.get(WEDDING_GIFT_REGISTRY_CART_ID_QUERY_PARAM) ?? queryParams.get(MULTI_CART_ID_QUERY_PARAM) ?? undefined, [queryParams])
  const pathName = useAppSelector(state => state.router.location.pathname)
  const giftRegistryCartPathSlug = useMemo(() => {
    const match = pathName.match(WEDDING_GIFT_REGISTRY_CART_PATH_REGEXP)
    return match?.[1]
  }, [pathName])
  const [storedSession, setStoredSession] = useState<WeddingGiftRegistrySession | undefined>(() => {
    const stored = sessionStorage.get(WEDDING_GIFT_REGISTRY_SESSION_KEY) as WeddingGiftRegistrySession | null
    return stored ?? undefined
  })
  const [requestedGiftRegistryCartId, setRequestedGiftRegistryCartId] = useState<string | undefined>()
  const accountId = useAppSelector(state => state.auth.account.memberId)
  const effectiveSlug = querySlug ?? giftRegistryCartPathSlug ?? storedSession?.slug
  const { data: wedding, isLoading: isWeddingLoading } = useWedding(effectiveSlug, { enabled: !!accountId && !!effectiveSlug })

  const effectiveVisibleCart = useSelector(multiCartActorRef, selectEffectiveVisibleCart)

  const showModal = useContext(ModalContext)
  const dispatch = useAppDispatch()
  const shouldShowWelcomeModal = useRef(true)

  const selectDefaultCart = useCallback(() => {
    if (!effectiveVisibleCart) return

    multiCartActorRef.send({
      type: 'set active cart',
      cartId: effectiveVisibleCart?.cartId,
    })
  }, [multiCartActorRef, effectiveVisibleCart])

  const clearContext = useCallback(() => {
    sessionStorage.remove(WEDDING_GIFT_REGISTRY_SESSION_KEY)
    setStoredSession(undefined)
    setRequestedGiftRegistryCartId(undefined)
    selectDefaultCart()
  }, [selectDefaultCart])

  // Persist wedding gift registry session to session storage when navigating to a wedding gift registry page
  useEffect(() => {
    if (!effectiveSlug) return

    let sessionGiftRegistryCartId = queryGiftRegistryCartId
    if (!sessionGiftRegistryCartId && storedSession?.slug === effectiveSlug) {
      sessionGiftRegistryCartId = storedSession.giftRegistryCartId
    }

    const nextSession = {
      slug: effectiveSlug,
      giftRegistryCartId: sessionGiftRegistryCartId,
    }
    sessionStorage.set(WEDDING_GIFT_REGISTRY_SESSION_KEY, nextSession)
    setStoredSession((prev) => {
      if (prev?.slug === nextSession.slug && prev?.giftRegistryCartId === nextSession.giftRegistryCartId) {
        return prev
      }
      return nextSession
    })
  }, [effectiveSlug, queryGiftRegistryCartId, storedSession?.giftRegistryCartId, storedSession?.slug])

  // Clear wedding context and session storage when navigating away from wedding setup pages
  useEffect(() => {
    const isWeddingSetupPage = WEDDING_SETUP_EDIT_PATH_REGEXP.test(pathName)
    if (!isWeddingSetupPage) return

    clearContext()
  }, [pathName, clearContext])

  const contextWedding = useMemo(() => {
    if (value?.wedding) return value.wedding
    if (accountId && wedding && accountId !== wedding.createdByUserId) return undefined
    return wedding
  }, [accountId, value?.wedding, wedding])

  let storedGiftRegistryCartId: string | undefined
  if (storedSession && storedSession.slug === effectiveSlug) {
    storedGiftRegistryCartId = storedSession.giftRegistryCartId
  }

  const giftRegistryCartId = value?.giftRegistryCartId ?? contextWedding?.giftRegistryCartId ?? storedGiftRegistryCartId
  const isActive = value?.isActive ?? !!contextWedding
  const isLoading = !!effectiveSlug && (!accountId || isWeddingLoading)
  const {
    isMultiCartMachineReady,
    isFetchingGiftRegistryCartData,
    hasLoadedGiftRegistryCartItems,
  } = useSelector(multiCartActorRef, (snapshot) => {
    const cartItems = snapshot.context.cartItems

    return {
      isMultiCartMachineReady: snapshot.matches('ready'),
      isFetchingGiftRegistryCartData: snapshot.matches('fetchingGivenCartData'),
      hasLoadedGiftRegistryCartItems: !!giftRegistryCartId && cartItems.status === 'loaded' && !!cartItems.data[giftRegistryCartId],
    }
  })

  const showWelcomeModal = useCallback(async() => {
    if (!wedding) return

    shouldShowWelcomeModal.current = false

    const result = await showModal<WeddingGiftRegistryWelcomeModalResult>(<WeddingGiftRegistryWelcomeModal />)
    if (result === 'back_to_setup') {
      dispatch(replaceWithRegion(`/weddings/${wedding.slug}/edit`))
    }
  }, [showModal, dispatch, wedding])

  useEffect(() => {
    if (!shouldShowWelcomeModal.current) return
    showWelcomeModal()
  }, [wedding, showWelcomeModal])

  useEffect(() => {
    if (!giftRegistryCartId) return
    if (!isMultiCartMachineReady) return
    if (isFetchingGiftRegistryCartData) return
    if (hasLoadedGiftRegistryCartItems) return
    if (requestedGiftRegistryCartId === giftRegistryCartId) return

    setRequestedGiftRegistryCartId(giftRegistryCartId)
    multiCartActorRef.send({
      type: 'set active cart',
      cartId: giftRegistryCartId,
      persist: false,
    })
    multiCartActorRef.send({
      type: 'fetch cart data',
      cartId: giftRegistryCartId,
    })
  }, [
    giftRegistryCartId,
    hasLoadedGiftRegistryCartItems,
    isFetchingGiftRegistryCartData,
    isMultiCartMachineReady,
    multiCartActorRef,
    requestedGiftRegistryCartId,
  ])

  const addItems = useCallback((items: Array<App.Checkout.AnyItem>, options: AddItemsOptions = {}) => {
    if (!giftRegistryCartId) {
      showSnackbar(
        intl.formatMessage({
          id: 'lCV0coS0U8JcVBZyi6eO',
          defaultMessage: 'We could not find your gift registry cart. Please return to setup and try again.',
          description: 'Error snackbar shown when adding an offer to a wedding gift registry cart without a cart id.',
        }),
        'critical',
      )
      return false
    }

    if (!effectiveSlug) {
      showSnackbar(
        intl.formatMessage({
          id: 'xHOPgKjp_YA8gvayb_Dz',
          defaultMessage: 'We could not find your wedding registry. Please return to setup and try again.',
          description: 'Error snackbar shown when syncing wedding gift registry cart items without a wedding id.',
        }),
        'critical',
      )
      return false
    }

    const itemsReadyToAdd = items
      .filter(item => isWeddingGiftRegistryItemSupported(item.itemType))
      .filter(item => (item.itemType === 'flight' && item.flights.length > 0) || item.itemType !== 'flight')
      .map(prepareItem)

    if (itemsReadyToAdd.length === 0) {
      showSnackbar(
        intl.formatMessage({
          id: '4oBSRixPr3rhf-oBNrQ8',
          defaultMessage: 'We could not add this offer to your gift registry cart.',
          description: 'Error snackbar shown when an offer produces no valid items for the wedding gift registry cart.',
        }),
        'critical',
      )
      return false
    }

    getCartMachineActor()?.send({
      type: 'add items to cart',
      cartId: giftRegistryCartId,
      items: itemsReadyToAdd,
      skipNotification: true,
      onSuccess: () => {
        options.onAdd?.(itemsReadyToAdd)

        if (!options.skipNotification) {
          showSnackbar(
            intl.formatMessage({
              id: 'mkChqgx9mbAowtUfXcwV',
              defaultMessage: 'Item added to gift registry cart',
              description: 'Success snackbar message when an item is added to a wedding gift registry cart',
            }),
            'success',
            {
              icon: successIcon,
              action: {
                label: intl.formatMessage({
                  id: 'pzf0j6LnELDH6bTg7JZ0',
                  defaultMessage: 'View cart',
                  description: 'Snackbar action label to navigate to the wedding gift registry cart page after adding an item',
                }),
                to: getWeddingGiftRegistryCartPath(effectiveSlug),
              },
            },
          )
        }
      },
    })

    return true
  }, [effectiveSlug, giftRegistryCartId, intl])

  const addToWeddingGiftRegistryCartCTALabel = intl.formatMessage({
    id: '9cGieJckPuSJOgd3hpfS',
    defaultMessage: 'Add to gift registry cart',
    description: 'Primary CTA button text to add offer to wedding gift registry cart',
  })

  const addToWeddingGiftRegistryCartCTALabelShort = intl.formatMessage({
    id: 'zYmzIHhjbiukLs-Xh4c3',
    defaultMessage: 'Add',
    description: 'Primary CTA button short text to add offer to wedding gift registry cart',
  })

  const providerValue = useMemo(() => ({
    isActive,
    isLoading,
    wedding: contextWedding,
    giftRegistryCartId,
    addItems,
    clearContext,
    addToWeddingGiftRegistryCartCTALabel,
    addToWeddingGiftRegistryCartCTALabelShort,
  }), [addItems, clearContext, contextWedding, giftRegistryCartId, isActive, isLoading, addToWeddingGiftRegistryCartCTALabel, addToWeddingGiftRegistryCartCTALabelShort])

  return (
    <WeddingGiftRegistryContext.Provider value={providerValue}>
      {children}
    </WeddingGiftRegistryContext.Provider>
  )
}

export function useWeddingGiftRegistryContext() {
  return useContext(WeddingGiftRegistryContext)
}

export default WeddingGiftRegistryProvider
